/**
 * OrganizationContext — API-driven organization context.
 *
 * Fetches the user's organizations from the backend API, respecting role-based
 * access control:
 *   - super_admin / ops_admin: sees all organizations (GET /api/cbos)
 *   - Other roles: sees only their assigned organizations (GET /api/cbos/users/me/organizations)
 *
 * The selected organization is synced with localStorage (active_cbo_id) and
 * dispatches events so other contexts (Branding, Role, etc.) react to org changes.
 *
 * Based on the mobile app's OrganizationContext pattern.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { organizationsAPI } from '@/services/api/organizations';

interface Organization {
  id: number;
  name: string;
  description?: string;
  contact_email?: string;
  status?: string;
  settings?: any;
  sponsor_type?: string | null;
  customer_status?: string;
  is_test_data?: boolean;
  [key: string]: any;
}

interface OrganizationContextType {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  loading: boolean;
  error: string | null;
  setSelectedOrganization: (org: Organization | null) => void;
  refetch: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType>({
  organizations: [],
  selectedOrganization: null,
  loading: false,
  error: null,
  setSelectedOrganization: () => {},
  refetch: async () => {},
});

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, token } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganizationState] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = useCallback(async () => {
    if (!user || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user's organizations — backend handles role-based filtering:
      // super_admin/ops_admin see all orgs, others see only their assigned orgs
      const userOrgs = await organizationsAPI.getMyOrganizations();
      const orgs: Organization[] = userOrgs.map((uo: any) => uo.organization || uo);

      setOrganizations(orgs);

      // Resolve the active org from localStorage or default to primary
      const activeCboId = localStorage.getItem('active_cbo_id');
      const activeOrg = activeCboId
        ? orgs.find((o) => o.id === parseInt(activeCboId, 10))
        : null;
      const primaryOrg = orgs.find((o) => o.id === user.cbo_id);
      const defaultOrg = activeOrg || primaryOrg || orgs[0] || null;

      if (defaultOrg) {
        setSelectedOrganizationState(defaultOrg);
        localStorage.setItem('active_cbo_id', String(defaultOrg.id));
      }
    } catch (err: any) {
      console.error('OrganizationContext: Failed to fetch organizations', err);
      setError(err.message || 'Failed to load organizations');

      // Fallback: build minimal org from localStorage so pages can still render
      const cboId = localStorage.getItem('active_cbo_id');
      if (cboId) {
        setSelectedOrganizationState({
          id: parseInt(cboId, 10),
          name: localStorage.getItem('active_org_name') || 'Organization',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Listen for org changes from other parts of the app (org switcher, etc.)
  useEffect(() => {
    const handleOrgChange = () => {
      const cboId = localStorage.getItem('active_cbo_id');
      if (cboId && organizations.length > 0) {
        const org = organizations.find((o) => o.id === parseInt(cboId, 10));
        if (org) setSelectedOrganizationState(org);
      }
    };

    window.addEventListener('organizationChanged', handleOrgChange);
    window.addEventListener('storage', (e) => {
      if (e.key === 'active_cbo_id') handleOrgChange();
    });

    return () => {
      window.removeEventListener('organizationChanged', handleOrgChange);
    };
  }, [organizations]);

  const setSelectedOrganization = useCallback((org: Organization | null) => {
    setSelectedOrganizationState(org);
    if (org) {
      localStorage.setItem('active_cbo_id', String(org.id));
      localStorage.setItem('active_org_name', org.name);
      window.dispatchEvent(new Event('organizationChanged'));
    }
  }, []);

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        selectedOrganization,
        loading,
        error,
        setSelectedOrganization,
        refetch: fetchOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  return useContext(OrganizationContext);
}

export default OrganizationContext;
